import { useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import { FaFileLines, FaReceipt } from "react-icons/fa6";
import DialogModal from "../dialog-modal/dialog-modal.component";
import FormError from "../form-error/form-error.component";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { PRINT_SIZES } from "../../queries/bill.queries";
import type { PrintSize } from "../../queries/bill.queries";

// Sheet sizes (A4/A3/A5) vs thermal-roll sizes are physically different
// printer hardware — most businesses only own one kind — so the icon is
// there to make that distinction scannable at a glance, not decorative.
const SIZE_ICONS: Record<PrintSize, typeof FaFileLines> = {
	a4: FaFileLines,
	a3: FaFileLines,
	a5: FaFileLines,
	thermal_80: FaReceipt,
	thermal_58: FaReceipt,
};

interface PrintOptionsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initialSize: PrintSize;
	isPending: boolean;
	isError?: boolean;
	error?: unknown;
	onConfirm: (size: PrintSize) => void;
}

// Shared print-size chooser for single-bill/credit-note/draft printing
// (invoice-bill, direct-bill, draft-preview-panel) — one confirm click both
// picks the size and fires the actual PDF request, matching the "nothing
// fetches until an explicit action" rule this migration is built around.
// Bulk print deliberately does NOT use this — it's already its own modal, so
// it gets an inline size <select> instead of a second, stacked dialog.
export default function PrintOptionsDialog({
	open,
	onOpenChange,
	initialSize,
	isPending,
	isError,
	error,
	onConfirm,
}: PrintOptionsDialogProps) {
	const [size, setSize] = useState<PrintSize>(initialSize);

	// Dialog is mounted once per page and toggled via `open`, not
	// mounted/unmounted per print — resync the pre-selected size (last-used,
	// per useLastPrintSize) each time it's reopened.
	useEffect(() => {
		if (open) setSize(initialSize);
	}, [open, initialSize]);

	return (
		<DialogModal
			open={open}
			onOpenChange={onOpenChange}
			title="Choose print size"
			description="Select the paper size to generate the PDF for."
			footer={
				<>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
						Cancel
					</Button>
					<Button onClick={() => onConfirm(size)} disabled={isPending}>
						{isPending ? (
							<>
								<FiLoader className="animate-spin" /> Generating...
							</>
						) : (
							"Print"
						)}
					</Button>
				</>
			}
		>
			<div className="grid grid-cols-2 gap-2">
				{PRINT_SIZES.map((option) => {
					const Icon = SIZE_ICONS[option.id];
					return (
						<button
							key={option.id}
							type="button"
							onClick={() => setSize(option.id)}
							disabled={isPending}
							className={cn(
								"flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:pointer-events-none disabled:opacity-50",
								size === option.id
									? "border-primary bg-primary/10 text-foreground"
									: "border-border bg-background hover:bg-muted",
							)}
						>
							<Icon className="shrink-0 text-muted-foreground" />
							{option.label}
						</button>
					);
				})}
			</div>
			<FormError isError={isError} error={error} fallback="Failed to generate PDF. Please try again." />
		</DialogModal>
	);
}
